import React from "react"
import { graphql } from "gatsby"
import Layout from "@components/Layout"
import SliceZone from "../components/SliceZone"
import "@styles/components/homepage.scss"
import Seo from "@components/Seo"
import { organizationSchema } from "@utils/schemaBuilder"

const Homepage = ({ data }) => {
  const homepage = data.prismicHomepage.data
  return (
    <Layout className="home">
      <SliceZone sliceZone={homepage.body} />
    </Layout>
  )
}

export const Head = ({ location, data }) => {
  return (
    <Seo
      title={data.prismicHomepage.data.meta_title}
      description={data.prismicHomepage.meta_description}
      location={location}
    >
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema())}
      </script>
    </Seo>
  )
}

export const query = graphql`
  query HomepageQuery($id: String) {
    prismicHomepage(id: { eq: $id }) {
      _previewable
      uid
      data {
        meta_title
        meta_keywords
        meta_description
        body {
          ... on PrismicHomepageDataBodyHeroPostcodeCheck {
            id
            slice_type
            primary {
              cta_description {
                richText
                text
              }
              cta_text
              description {
                text
                richText
              }
              hero_image {
                alt
                gatsbyImageData(width: 1128, placeholder: NONE)
              }
              title
            }
          }
          ... on PrismicHomepageDataBodyColumnSection {
            id
            primary {
              number_of_columns
              columns_title {
                text
                richText
              }
              columns_subtitle {
                text
                richText
              }
              arrow_image
            }
            slice_type
            items {
              cta_url {
                url
                target
                link_type
              }
              cta_text {
                text
                richText
              }
              column_title {
                text
                richText
              }
              column_text {
                text
                richText
              }
              column_subtitle {
                text
                richText
              }
              column_image {
                gatsbyImageData(
                  placeholder: NONE
                  layout: CONSTRAINED
                  width: 200
                  height: 200
                )
                alt
              }
            }
          }
          ... on PrismicHomepageDataBodyTrustpilotScoreSection {
            id
            slice_type
            primary {
              trustpilot_title {
                text
                richText
              }
              trustpilot_subtitle {
                text
                richText
              }
              trustpilot_section_type
              trustpilot_cta_url {
                target
                url
                link_type
              }
              trustpilot_cta_text {
                text
                richText
              }
            }
          }
          ... on PrismicHomepageDataBodyBoxes {
            id
            slice_type
            primary {
              main_cta_label
              boxes_title: title {
                text
                richText
              }
              subtitle {
                text
                richText
              }
              main_cta_link {
                url
                target
                link_type
              }
            }
            items {
              cta_link {
                url
                link_type
                target
              }
              cta_label
              box_title {
                text
                richText
              }
              box_image {
                gatsbyImageData(placeholder: NONE)
                alt
              }
              box_description {
                text
                richText
              }
              box_annotation
            }
          }
          ... on PrismicHomepageDataBodyCuratedPostsSection {
            id
            slice_type
            items {
              post {
                url
                document {
                  ... on PrismicPost {
                    id
                    data {
                      title {
                        text
                        richText
                      }
                      short_description {
                        text
                        richText
                      }
                      image {
                        url
                        alt
                        gatsbyImageData(
                          width: 360
                          height: 360
                          placeholder: NONE
                        )
                      }
                      authors {
                        author {
                          document {
                            ... on PrismicAuthor {
                              id
                              url
                              data {
                                full_name {
                                  text
                                }
                              }
                            }
                          }
                        }
                      }
                      date(formatString: "D MMMM YYYY")
                    }
                    url
                  }
                  ... on PrismicRecipe {
                    id
                    data {
                      title {
                        text
                        richText
                      }
                      short_description {
                        text
                        richText
                      }
                      image {
                        url
                        alt
                        gatsbyImageData(
                          width: 360
                          height: 360
                          placeholder: NONE
                        )
                      }
                      authors {
                        author {
                          document {
                            ... on PrismicAuthor {
                              id
                              url
                              data {
                                full_name {
                                  text
                                }
                              }
                            }
                          }
                        }
                      }
                      date(formatString: "D MMMM YYYY")
                    }
                    url
                  }
                }
              }
            }
            primary {
              curated_posts_subtitle {
                text
                richText
              }
              curated_posts_title {
                text
                richText
              }
              curated_posts_cta_link {
                url
                link_type
                target
              }
              curated_posts_cta_label
            }
          }
          ... on PrismicHomepageDataBodyCuratedRecipeSection {
            id
            slice_type
            primary {
              curated_posts_title: curated_recipes_title {
                text
                richText
              }
              curated_posts_subtitle: curated_recipes_subtitle {
                text
                richText
              }
              curated_posts_cta_link: curated_recipes_cta_link {
                link_type
                url
                target
              }
              curated_posts_cta_label: curated_recipes_cta_label
            }
            items {
              post: recipe {
                document {
                  ... on PrismicRecipe {
                    id
                    data {
                      title {
                        text
                        richText
                      }
                      short_description {
                        text
                        richText
                      }
                      image {
                        url
                        alt
                        gatsbyImageData(
                          width: 360
                          height: 360
                          placeholder: NONE
                        )
                      }
                      authors {
                        author {
                          document {
                            ... on PrismicAuthor {
                              id
                              url
                              data {
                                full_name {
                                  text
                                }
                              }
                            }
                          }
                        }
                      }
                      date(formatString: "D MMMM YYYY")
                    }
                    url
                  }
                }
              }
            }
          }
          ... on PrismicHomepageDataBodyFeatureCard {
            id
            slice_type
            primary {
              feature_title {
                text
                richText
              }
              feature_image {
                alt
                gatsbyImageData(placeholder: NONE)
              }
              feature_description {
                text
                richText
              }
              feature_cta_link {
                url
                target
                link_type
              }
              feature_cta_label
            }
          }
          ... on PrismicHomepageDataBodyCommunitySection {
            id
            slice_type
            primary {
              community_title {
                text
                richText
              }
            }
            items {
              community_cta_annotation
              community_cta_label
              community_cta_link {
                url
                target
                link_type
              }
              community_description {
                text
                richText
              }
              community_image {
                gatsbyImageData(width: 360, height: 360, placeholder: NONE)
                alt
              }
            }
          }
        }
      }
    }
  }
`

export default Homepage
